import storeService from '../store/store.service';
import endpoint from '../util/endpoint';
import { resolveEndpoint } from '../util/resolveEndpoint';
import { takeSchummarPersisted } from '@/lib/takeSchummarPersisted';
import { errorToString } from '@whiz-cart/node-shared/errorToString';
import { service } from '@whiz-cart/node-shared/service/service';
import { AccessTokenRaw, LoginState, RoleScope } from '@whiz-cart/ui-shared/auth/authTypes';
import { request } from '@whiz-cart/ui-shared/request/request';
import { allResources, createCache, createStore, persist } from 'cross-state';
import { jwtDecode } from 'jwt-decode';
import localforage from 'localforage';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

class AuthService {
    constructor() {
        void this.ready.then(() => {
            this.claimMapping.state
                .map((state) => state.value)
                .subscribe((claimMapping) => {
                    if (claimMapping) this.loginState.set('claimMapping', claimMapping);
                });
        });

        // Persist does not sync removed keys, so a logout stores an empty refresh token. isLoggedIn is not persisted:
        // each context derives it, except while acceptLogin waits for the claims.
        this.loginState
            .map((state) => state.refreshToken)
            .subscribe(
                (refreshToken) => {
                    this.accessToken.invalidate();
                    const { isLoggedIn } = this.loginState.get();
                    if (!refreshToken && isLoggedIn) this.logout();
                    else if (refreshToken && isLoggedIn === false && !this.isAcceptingLogin) this.loginState.set('isLoggedIn', true);
                },
                { runNow: false },
            );
    }

    loginState = createStore<LoginState>({});

    private storage = localforage.createInstance({ name: 'auth' });
    private persistOptions = {
        id: 'auth',
        storage: this.storage,
        paths: ['refreshToken', 'deviceId', 'locale', 'tokenDetails', 'claimMapping'],
    } as const;
    private persistence = persist(this.loginState, this.persistOptions);
    private isAcceptingLogin = false;

    /** Resolves once the persisted login state, including the one taken over from schummar-state, is loaded. */
    readonly ready = this.persistence.initialized.then(async () => {
        if (!(await this.takeOverLegacy())) {
            this.loginState.set('isLoggedIn', !!this.loginState.get().refreshToken);
        }
    });

    /** Data in auth_v3 was written by a page still running the old code after any earlier takeover, so it wins. */
    private async takeOverLegacy() {
        const legacy = (await takeSchummarPersisted('auth_v3').catch((e) => {
            console.error('Failed to take over the old login state', e);
            return undefined;
        })) as LoginState | undefined;
        if (!legacy) return false;

        this.loginState.set({ ...legacy, isLoggedIn: this.loginState.get().isLoggedIn });
        if (legacy.refreshToken) this.loginState.set('isLoggedIn', true);
        else this.logout();
        return true;
    }

    accessToken = createCache(
        async () => {
            try {
                const accessToken = await this.fetchAccessToken();

                this.loginState.update((state) => {
                    state.tokenDetails = this.decodeToken(accessToken);
                });

                return accessToken;
            } catch (e: any) {
                if (e.response?.status === 401) {
                    this.logout();
                }

                const msg = errorToString(e);
                throw new Error(`Failed to get accessToken: ${msg}`);
            }
        },
        {
            invalidateAfter: (state) => {
                if (state.status !== 'value') {
                    return 10_000;
                }

                const { exp = 0 } = this.decodeToken(state.value);
                const timeLeft = exp * 1000 - Date.now();
                return Math.max(timeLeft * 0.9, 0);
            },
            invalidateOnWindowFocus: false,
        },
    );

    updateLanguage(language: string | undefined | null) {
        if (!language) {
            return;
        }
        this.loginState.update((state) => {
            state.locale = language;
        });
    }

    claimMapping = createCache(
        async () => {
            const response = await endpoint('storeManager.getClaims').get();
            const parsed = z
                .record(
                    z.string(),
                    z.record(
                        z.string(),
                        z.object({
                            allowAnyScope: z.boolean().catch(false),
                            mustBeEnabledForStore: z.boolean().catch(false),
                            claim: z.string(),
                            roles: z.string().array(),
                        }),
                    ),
                )
                .parse(response);

            return new Map(
                Object.values(parsed).flatMap((claims) => {
                    return Object.entries(claims).map(([key, value]) => [
                        key,
                        {
                            ...value,
                            roles: new Set(value.roles),
                        },
                    ]);
                }),
            );
        },
        { invalidateAfter: 60 * 60 * 1000, invalidateOnWindowFocus: false },
    );

    roleHierarchy = createCache(
        async () => {
            const response = await endpoint('storeManager.getRoles').get();
            return z.record(z.string(), z.string().array()).parse(response);
        },
        { invalidateAfter: 60 * 60 * 1000, invalidateOnWindowFocus: false },
    );

    isCaptchaEnabled = createCache(async () => {
        const response: boolean = await request.get(resolveEndpoint('storeManager.isCaptchaEnabled'));
        return response;
    });

    async login(userName: string, password: string, turnstileResponse?: string, testEnvironmentSecretToken?: string) {
        try {
            const headers: Record<string, string> = {};
            if (typeof testEnvironmentSecretToken === 'string') {
                headers['x-test-environment'] = testEnvironmentSecretToken;
            }

            const response: { accessToken: string; refreshToken: string; userLanguage: string | null } = await request.post(
                resolveEndpoint('storeManager.login'),
                {
                    userName,
                    password,
                    claims: await this.getLoginClaims(),
                    turnstileResponse,
                },
                { headers },
            );

            await this.acceptLogin(response);
        } catch (e) {
            console.error('Failed to login', e);
            throw e;
        }
    }

    /** Usually use authService.accessToken instead!
     * This fetches a new token ignoring the cache.
     */
    async fetchAccessToken() {
        await this.ready;
        const { refreshToken } = this.loginState.get();

        if (!refreshToken) {
            console.debug('No refreshToken available. Could not fetch accessToken');
            throw Error('No refreshToken available. Could not fetch accessToken');
        }

        try {
            const { accessToken, userLanguage }: { accessToken: string; userLanguage: string | null } = await request.post(
                resolveEndpoint('storeManager.refresh'),
                {
                    refreshToken,
                    claims: await this.getLoginClaims(),
                },
            );
            authService.updateLanguage(userLanguage);
            return accessToken;
        } catch (e: any) {
            console.error('Failed to retreive accessToken', e.response?.status ?? '<no status>', e);
            throw e;
        }
    }

    async acceptLogin({
        accessToken,
        refreshToken,
        userLanguage,
    }: {
        accessToken: string;
        refreshToken: string;
        userLanguage: string | null;
    }) {
        this.isAcceptingLogin = true;
        try {
            this.loginState.update((state) => {
                state.refreshToken = refreshToken;
                state.tokenDetails = this.decodeToken(accessToken);
            });
        } finally {
            this.isAcceptingLogin = false;
        }
        authService.updateLanguage(userLanguage);

        allResources.clearAll();
        this.accessToken.updateValue(accessToken);

        await Promise.all([this.claimMapping.get(), storeService.allStores().get()]);

        this.loginState.set('isLoggedIn', true);

        console.debug('Logged in.');
    }

    logout() {
        this.loginState.update((state) => {
            state.isLoggedIn = false;
            state.refreshToken = '';
            state.tokenDetails = undefined;
        });
        this.accessToken.clear();
        console.debug('Logged out.');
    }

    async changePassword(userName: string, oldPassword: string, newPassword: string) {
        try {
            await request.post(resolveEndpoint('storeManager.changePassword'), {
                userName,
                password: oldPassword,
                newPassword,
            });
        } catch (e) {
            console.error('Failed to change password', e);
            throw e;
        }
    }

    async getLoginClaims() {
        await this.ready;
        let deviceId = this.loginState.get().deviceId;

        if (!deviceId) {
            deviceId = uuid();
            this.loginState.update((state) => {
                state.deviceId = deviceId;
            });
        }

        return { deviceId };
    }

    decodeToken(accessToken: string) {
        const raw: AccessTokenRaw = jwtDecode(accessToken);

        return {
            ...raw,
            roles: typeof raw.roles === 'string' ? JSON.parse(raw.roles) : (raw.roles ?? []),
        };
    }

    assumeRole(role: string | RoleScope) {
        this.loginState.update((state) => {
            state.tokenDetails!.roles = [typeof role === 'string' ? { scope: 'global', roles: [role] } : role];
        });
    }

    private refreshing?: Promise<void>;

    /** Re-reads the login state from storage. The service worker calls this before its requests, so a login change
     * a window broadcast just before is not missed.
     */
    refreshLoginState() {
        return (this.refreshing ??= (async () => {
            await this.ready;
            await this.persistence.stop();
            this.persistence = persist(this.loginState, this.persistOptions);
            await this.persistence.initialized;
            await this.takeOverLegacy();
        })().finally(() => {
            this.refreshing = undefined;
        }));
    }
}

export const authService = service('authService', AuthService);
